import { useCallback } from "react";
import { useDeleteScheduleApplicationMutation } from "@api/scheduleTemplate.api";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { ScheduleDeleteJournalEntryMode } from "@common/types/domains/accounting/schedule";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    ShowLockedPeriods,
    useScheduleLockedPeriodsDialog,
} from "@components/accounting/schedules/useScheduleLockedPeriodsDialog";
import { invalidateTransactionDetailsQueries } from "@components/transactions/TransactionDetails/useTransactionDetailsQuery";
import {
    invalidateTransactionsQueries,
    updateTransactionInCache,
} from "@components/transactions/useTransactionsQuery";

/**
 * Removal through the lifecycle `delete` verb, which replaced the bulk
 * unapply: unapply refuses once any journal entry exists, and apply posts the
 * whole ladder, so it could never succeed on an applied schedule.
 */
export function useRemoveTransactionSchedule(
    transaction: Pick<TransactionListItem, "id" | "entity" | "schedule">,
    options: { showLockedPeriods?: ShowLockedPeriods } = {},
) {
    const { error } = useToaster();
    const mutation = useDeleteScheduleApplicationMutation();
    const showLockedPeriods = useScheduleLockedPeriodsDialog(
        options.showLockedPeriods,
    );
    const schedule = transaction.schedule;

    const removeCurrent = useCallback(
        async (
            journalEntryMode: ScheduleDeleteJournalEntryMode,
        ): Promise<boolean> => {
            if (schedule == null) {
                return false;
            }
            try {
                await mutation.mutateAsync({
                    applicationId: schedule.scheduleApplicationId,
                    entityId: transaction.entity.id,
                    journalEntryMode,
                });
                // A retired application drops out of the column's status filter,
                // so the row shows no schedule again.
                updateTransactionInCache(transaction.id, (current) => ({
                    ...current,
                    schedule: undefined,
                }));
                await Promise.all([
                    invalidateTransactionsQueries(),
                    invalidateTransactionDetailsQueries(transaction.id),
                ]);
                return true;
            } catch (removeError) {
                if (!showLockedPeriods(removeError)) {
                    error(
                        removeError instanceof Error
                            ? removeError.message
                            : "Failed to remove schedule",
                    );
                }
                return false;
            }
        },
        [
            error,
            mutation,
            schedule,
            showLockedPeriods,
            transaction.entity.id,
            transaction.id,
        ],
    );

    return { removeCurrent, isRemoving: mutation.isPending };
}
